import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { GenerateUrlRequestBody, Paginated, PaginationQuery, UpdateOrderRequestBody } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import {
  FullCategory,
  SearchCategory,
  UpsertCategoryNewImageBody,
  UpsertCategoryRemoveImageBody,
} from "./types/categories";

export const useCategoriesApi = () => {
  const api = useApi();

  // Get a single category
  const getAdminCategory = (id: string, signal: AbortSignal) => {
    return useSWRMutation<FullCategory, CustomError, string>(`/admin/categories/${id}`, (url) =>
      api.get<FullCategory>({ url, signal }),
    );
  };

  // Get a paginated list of categories
  const useAdminCategories = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl("/admin/categories", query);
    return useSWR<Paginated<SearchCategory>, CustomError, string>(url, (url) =>
      api.get<Paginated<SearchCategory>>({ url, signal }),
    );
  };

  // Update an existing category
  const updateAdminCategory = useCallback(
    async (categoryId: string, body: UpsertCategoryRemoveImageBody | UpsertCategoryNewImageBody): Promise<void> => {
      const url = `/admin/categories/${categoryId}`;
      await api.put<UpsertCategoryRemoveImageBody | UpsertCategoryNewImageBody>(url, body);
      mutateCache(["/admin/categories", "/categories"], url);
    },
    [],
  );

  // generate image url
  const generateUploadImageUrl = useCallback(
    async (categoryId: string, fileName: string, fileType: string): Promise<string> => {
      const url = `/admin/categories/${categoryId}/image/generate_upload_url`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [],
  );

  const updateAdminCategoryOrder = useCallback(
    async (categoryId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/categories/${categoryId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/categories", "/categories"]);
    },
    [],
  );

  // Get a list of categories
  const useCategories = (query: PaginationQuery, signal: AbortSignal) => {
    const url = buildUrl("/categories", query);
    return useSWR<Paginated<SearchCategory>, CustomError, string>(url, (url) =>
      api.get<Paginated<SearchCategory>>({ url, signal }),
    );
  };

  return {
    getAdminCategory,
    useAdminCategories,
    updateAdminCategory,
    generateUploadImageUrl,
    updateAdminCategoryOrder,

    useCategories,
  };
};
